import React, { useState } from 'react';
import {
    Users,
    Activity,
    Settings,
    UserPlus,
    Mail,
    Phone,
    Trash2,
    Calendar,
    Briefcase,
    Shield
} from 'lucide-react';
import { toast } from 'react-hot-toast';

const TeamDetailsPanel = ({
    team,
    members = [],
    activity = [],
    loading,
    onAddMember,
    onRemoveMember,
    onEditTeam
}) => {
    const [activeTab, setActiveTab] = useState('members');

    if (loading) {
        return (
            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 min-h-[400px] flex items-center justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
        );
    }

    if (!team) {
        return (
            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 min-h-[400px] flex flex-col items-center justify-center text-center">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center text-gray-400 mb-4">
                    <Users size={32} />
                </div>
                <h3 className="text-lg font-semibold text-gray-800">Select a team</h3>
                <p className="text-gray-500 max-w-xs mt-2">
                    Click on a team in the hierarchy to view its members, recent activity, and settings.
                </p>
            </div>
        );
    }

    return (
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm flex flex-col h-full min-h-[500px]">
            {/* Header */}
            <div className="p-6 border-b border-gray-100">
                <div className="flex items-start justify-between">
                    <div className="flex items-center gap-4">
                        <div
                            className="w-16 h-16 rounded-2xl flex items-center justify-center text-white shadow-sm"
                            style={{ backgroundColor: team.color || '#3b82f6' }}
                        >
                            <Users size={32} />
                        </div>
                        <div>
                            <h2 className="text-xl font-bold text-gray-900">{team.team_name}</h2>
                            {team.description && (
                                <p className="text-gray-500 mt-1 text-sm">{team.description}</p>
                            )}
                            <div className="flex items-center gap-3 mt-2 text-sm text-gray-500">
                                <span>{members.length} members</span>
                                {team.leader_name && (
                                    <>
                                        <span>•</span>
                                        <span className="flex items-center gap-1">
                                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
                                            Lead: {team.leader_name}
                                        </span>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Tabs */}
            <div className="flex px-6 border-b border-gray-100 overflow-x-auto whitespace-nowrap">
                <button
                    onClick={() => setActiveTab('members')}
                    className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${activeTab === 'members'
                        ? 'border-primary text-primary'
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                        }`}
                >
                    <Users size={16} />
                    Members
                </button>
                <button
                    onClick={() => setActiveTab('activity')}
                    className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${activeTab === 'activity'
                        ? 'border-primary text-primary'
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                        }`}
                >
                    <Activity size={16} />
                    Activity
                </button>
                <button
                    onClick={() => setActiveTab('settings')}
                    className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${activeTab === 'settings'
                        ? 'border-primary text-primary'
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                        }`}
                >
                    <Settings size={16} />
                    Settings
                </button>
            </div>

            {/* Content */}
            <div className="p-6 flex-1 bg-gray-50/50">
                {activeTab === 'members' && (
                    <div className="space-y-4">
                        <div className="flex justify-between items-center">
                            <h3 className="font-semibold text-gray-900">Team Members</h3>
                            <button
                                onClick={() => onAddMember(team)}
                                className="px-3 py-1.5 bg-white border border-gray-200 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 hover:border-gray-300 transition-all shadow-sm flex items-center gap-2"
                            >
                                <UserPlus size={16} />
                                Add Member
                            </button>
                        </div>

                        {members.length === 0 ? (
                            <div className="bg-white rounded-xl p-8 text-center border border-gray-100">
                                <Users size={40} className="mx-auto text-gray-300 mb-3" />
                                <p className="text-gray-500">No members in this team yet.</p>
                                <button
                                    onClick={() => onAddMember(team)}
                                    className="mt-3 text-primary font-medium text-sm hover:underline"
                                >
                                    Add members
                                </button>
                            </div>
                        ) : (
                            <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden overflow-x-auto">
                                <table className="w-full min-w-[600px]">
                                    <thead className="bg-gray-50 border-b border-gray-100">
                                        <tr>
                                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Employee</th>
                                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Contact</th>
                                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Role</th>
                                            <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-100">
                                        {members.map((member) => (
                                            <tr key={member.id} className="hover:bg-gray-50/50">
                                                <td className="px-4 py-3">
                                                    <div className="flex items-center gap-3">
                                                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary-light to-primary flex items-center justify-center text-white text-xs font-bold">
                                                            {member.first_name?.[0]}{member.last_name?.[0]}
                                                        </div>
                                                        <div>
                                                            <p className="font-medium text-gray-900 text-sm">
                                                                {member.first_name} {member.last_name}
                                                            </p>
                                                            <p className="text-xs text-gray-500">{member.position || 'No Title'}</p>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="px-4 py-3">
                                                    <div className="flex items-center gap-2 text-xs text-gray-600">
                                                        <Mail size={12} />
                                                        {member.email}
                                                    </div>
                                                </td>
                                                <td className="px-4 py-3">
                                                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${member.id === team.leader_id
                                                        ? 'bg-purple-100 text-purple-700'
                                                        : 'bg-gray-100 text-gray-600'
                                                        }`}>
                                                        {member.id === team.leader_id ? 'Team Lead' : 'Member'}
                                                    </span>
                                                </td>
                                                <td className="px-4 py-3 text-right">
                                                    <button
                                                        onClick={() => {
                                                            if (window.confirm(`Remove ${member.first_name} from ${team.team_name}?`)) {
                                                                onRemoveMember(team.id, member.id);
                                                            }
                                                        }}
                                                        className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                                                        title="Remove from team"
                                                    >
                                                        <Trash2 size={16} />
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                )}

                {activeTab === 'activity' && (
                    <div className="space-y-4">
                        <h3 className="font-semibold text-gray-900">Recent Activity</h3>
                        {activity.length === 0 ? (
                            <div className="bg-white rounded-xl p-8 text-center border border-gray-100">
                                <Activity size={32} className="mx-auto text-gray-300 mb-3" />
                                <p className="text-gray-500">No recent activity for this team.</p>
                            </div>
                        ) : (
                            <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                                <div className="divide-y divide-gray-100">
                                    {activity.map((item, index) => (
                                        <div key={index} className="p-4 flex gap-3 hover:bg-gray-50/50 transition-colors">
                                            <div className="w-8 h-8 rounded-full bg-blue-50 flex items-center justify-center shrink-0">
                                                <Activity size={16} className="text-blue-500" />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p className="text-sm text-gray-900 font-medium">
                                                    {item.description}
                                                </p>
                                                <div className="flex items-center gap-2 mt-1">
                                                    <p className="text-xs text-gray-500">
                                                        {new Date(item.created_at).toLocaleDateString()} at {new Date(item.created_at).toLocaleTimeString()}
                                                    </p>
                                                    {item.performed_by && (
                                                        <>
                                                            <span className="text-gray-300">•</span>
                                                            <p className="text-xs text-gray-500">by {item.performed_by}</p>
                                                        </>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {activeTab === 'settings' && (
                    <div className="max-w-xl">
                        <h3 className="font-semibold text-gray-900 mb-4">Team Settings</h3>

                        <div className="bg-white p-4 rounded-xl border border-gray-200 space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Team Name</label>
                                <div className="text-gray-900">{team.team_name}</div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                                <div className="text-gray-900">{team.description || 'No description'}</div>
                            </div>

                            <div className="pt-4 mt-4 border-t border-gray-100">
                                <button
                                    onClick={() => onEditTeam(team)}
                                    className="px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
                                >
                                    Edit Team Details
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default TeamDetailsPanel;
